import { useRef, useState, useEffect } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import {
  FileText,
  Stethoscope,
  HeartHandshake,
  Mic,
  Brain,
  Clock,
  ShieldCheck,
  Languages,
  MessageSquare,
  CalendarCheck,
  Pill,
  BellRing,
  Sparkles,
  ArrowRight,
  Zap,
} from "lucide-react";

const fade = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const agents = [
  {
    id: "mitra",
    name: "Dr. Mitra",
    role: "Clinical Summary AI",
    tagline: "Instant patient timelines for every consult",
    photo:
      "https://images.unsplash.com/photo-1659353886973-ced1dfeab3ac?w=500&h=600&fit=crop&crop=top",
    accent: "hsl(152 73% 43%)",
    accentClass: "text-primary",
    accentBg: "bg-primary",
    accentBgLight: "bg-primary/10",
    ringClass: "ring-primary/40",
    desc: "Dr. Mitra assembles fragmented records into a clean, 5-second clinical timeline. Summaries are read-only and non-diagnostic, built for trust.",
    capabilities: [
      { icon: FileText, label: "5-sec timeline generation" },
      { icon: Brain, label: "Read-only AI summaries" },
      { icon: Clock, label: "Saves 2+ min per consult" },
      { icon: ShieldCheck, label: "Non-diagnostic, trust-safe" },
    ],
    stat: "2+ min",
    statLabel: "saved per consult",
  },
  {
    id: "context",
    name: "Dr. Jeevan Rakshak",
    role: "History Assembly AI",
    tagline: "Complete clinical history, assembled instantly",
    photo:
      "https://images.unsplash.com/photo-1659353889416-fab1265eaa7f?w=500&h=600&fit=crop&crop=top",
    accent: "hsl(180 55% 38%)",
    accentClass: "text-chart-2",
    accentBg: "bg-chart-2",
    accentBgLight: "bg-chart-2/10",
    ringClass: "ring-chart-2/40",
    desc: "Dr. Jeevan Rakshak structures patient data from ABDM, labs, past records, and prescriptions into readable timelines for doctors, reducing cognitive load by 40% and ensuring no doctor ever asks twice.",
    capabilities: [
      { icon: Stethoscope, label: "Cross-provider record merge" },
      { icon: Languages, label: "Multilingual support" },
      { icon: MessageSquare, label: "Structured patient intake" },
      { icon: ShieldCheck, label: "ABDM-native integration" },
    ],
    stat: "40%",
    statLabel: "more clinical context",
  },
  {
    id: "helper",
    name: "Dr. Saathi",
    role: "Patient Support AI",
    tagline: "Always-on care companion for every patient",
    photo:
      "https://images.unsplash.com/photo-1741707039536-113e200f9e0d?w=500&h=600&fit=crop&crop=top",
    accent: "hsl(38 85% 52%)",
    accentClass: "text-chart-4",
    accentBg: "bg-chart-4",
    accentBgLight: "bg-chart-4/10",
    ringClass: "ring-chart-4/40",
    desc: "Dr. Saathi handles post-visit reminders, medication alerts, follow-ups, and loyalty incentives via AI-assisted calls and chat, boosting patient retention by 30% and reducing no-shows.",
    capabilities: [
      { icon: Mic, label: "Voice-enabled intake" },
      { icon: Pill, label: "Medication reminders" },
      { icon: CalendarCheck, label: "Follow-up scheduling" },
      { icon: BellRing, label: "Mental health check-ins" },
    ],
    stat: "24/7",
    statLabel: "patient support",
  },
];

const AgentPhoto = ({ agent }) => (
  <motion.div
    className="relative flex items-center justify-center"
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.9 }}
    transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
  >
    {/* Outer glow ring */}
    <motion.div
      className="absolute inset-0 rounded-3xl"
      style={{
        background: `radial-gradient(circle at center, ${agent.accent}15, transparent 70%)`,
      }}
      animate={{ scale: [1, 1.05, 1], opacity: [0.5, 0.8, 0.5] }}
      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
    />

    {/* Photo container */}
    <div
      className={`relative w-56 h-56 sm:w-64 sm:h-64 lg:w-72 lg:h-72 rounded-3xl overflow-hidden ring-4 ${agent.ringClass} shadow-lg`}
    >
      <img
        src={agent.photo}
        alt={agent.name}
        className="w-full h-full object-cover object-top"
        data-testid={`agent-photo-${agent.id}`}
      />
      {/* Gradient overlay at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/40 to-transparent" />
      {/* Name badge on photo */}
      <div className="absolute bottom-3 left-3 right-3">
        <p className="text-white font-heading font-bold text-sm">{agent.name}</p>
        <p className="text-white/70 text-xs">{agent.role}</p>
      </div>
    </div>

    {/* AI Active indicator */}
    <motion.div
      className={`absolute -top-2 -right-2 flex items-center gap-1.5 px-3 py-1.5 rounded-full ${agent.accentBg} text-white shadow-lg`}
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
    >
      <motion.div
        className="w-2 h-2 rounded-full bg-white"
        animate={{ scale: [1, 1.3, 1], opacity: [1, 0.7, 1] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      />
      <span className="text-[10px] font-semibold tracking-wide">AI ACTIVE</span>
    </motion.div>

    {/* Floating sparkle */}
    <motion.div
      className="absolute -bottom-3 -left-3"
      animate={{ y: [0, -6, 0], rotate: [0, 10, 0] }}
      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
    >
      <div
        className={`w-10 h-10 rounded-xl ${agent.accentBgLight} flex items-center justify-center shadow-sm backdrop-blur-sm`}
      >
        <Zap className={`w-5 h-5 ${agent.accentClass}`} />
      </div>
    </motion.div>
  </motion.div>
);

const AgentCapabilities = ({ agent }) => (
  <motion.div
    className="flex flex-col justify-center"
    initial={{ opacity: 0, x: 30 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -30 }}
    transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
  >
    {/* Tagline */}
    <motion.p
      className={`text-xs font-semibold uppercase tracking-widest ${agent.accentClass} mb-2`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.2 }}
    >
      {agent.role}
    </motion.p>

    <motion.h3
      className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-2"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
    >
      {agent.name}
    </motion.h3>

    <motion.p
      className="text-sm text-muted-foreground leading-relaxed mb-6 max-w-md"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.25 }}
    >
      {agent.desc}
    </motion.p>

    {/* Stat highlight */}
    <motion.div
      className={`inline-flex items-center gap-3 px-4 py-3 rounded-2xl ${agent.accentBgLight} border border-transparent mb-6 w-fit`}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.3 }}
    >
      <span className={`text-3xl font-heading font-bold ${agent.accentClass}`}>
        {agent.stat}
      </span>
      <span className="text-xs text-muted-foreground leading-tight">{agent.statLabel}</span>
    </motion.div>

    {/* Capability items */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {agent.capabilities.map((cap, j) => (
        <motion.div
          key={cap.label}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: j * 0.1 + 0.35, duration: 0.4 }}
          className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 hover:border-primary/20 hover:bg-white/10 transition-all duration-300 group cursor-default"
          data-testid={`capability-${agent.id}-${j}`}
        >
          <div
            className={`w-9 h-9 rounded-lg ${agent.accentBgLight} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}
          >
            <cap.icon className={`w-4 h-4 ${agent.accentClass}`} />
          </div>
          <span className="text-xs font-medium text-foreground">{cap.label}</span>
        </motion.div>
      ))}
    </div>
  </motion.div>
);

export const AgentsSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [active, setActive] = useState("mitra");
  const agent = agents.find((a) => a.id === active);

  // Auto-cycle agents every 6 seconds
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setActive((prev) => {
        const idx = agents.findIndex((a) => a.id === prev);
        return agents[(idx + 1) % agents.length].id;
      });
    }, 6000);
    return () => clearInterval(timer);
  }, [paused]);

  return (
    <section
      id="agents"
      className="py-16 lg:py-20 section-bold relative overflow-hidden"
      ref={ref}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="agents-section"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.08, 0.15, 0.08] }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute top-[10%] right-[5%] w-80 h-80 rounded-full bg-primary/10 blur-3xl"
        />
        <motion.div
          animate={{ scale: [1, 1.15, 1], opacity: [0.06, 0.12, 0.06] }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute bottom-[10%] left-[5%] w-64 h-64 rounded-full bg-chart-2/10 blur-3xl"
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          variants={fade}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/8 border border-primary/15 mb-4">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            <span className="text-spaced text-primary !text-[10px]">
              A I &nbsp; A G E N T S
            </span>
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4"
            data-testid="agents-section-title"
          >
            Meet Our AI Agents
          </h2>
          <p className="text-base text-muted-foreground max-w-xl mx-auto">
            Three specialized AI agents working together to transform every healthcare
            interaction.
          </p>
        </motion.div>

        {/* Agent Tab Buttons — Confido-style */}
        <motion.div
          variants={fade}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          custom={1}
          className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-14"
        >
          {agents.map((a) => {
            const isActive = a.id === active;
            return (
              <motion.button
                key={a.id}
                onClick={() => setActive(a.id)}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className={`relative flex items-center gap-3 px-5 py-3.5 rounded-2xl border-2 transition-all duration-400 w-full sm:w-auto ${
                  isActive
                    ? "bg-white/10 border-primary/40 shadow-teal backdrop-blur-sm"
                    : "bg-white/5 border-white/10 hover:border-white/20 shadow-sm hover:shadow-md backdrop-blur-sm"
                }`}
                data-testid={`agent-tab-${a.id}`}
              >
                {/* Thumbnail */}
                <div
                  className={`w-11 h-11 rounded-xl overflow-hidden flex-shrink-0 ring-2 transition-all duration-300 ${
                    isActive ? "ring-primary/50" : "ring-transparent"
                  }`}
                >
                  <img
                    src={a.photo}
                    alt={a.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="text-left">
                  <p className="text-[10px] text-white/50 font-medium">Start with</p>
                  <p
                    className={`text-sm font-heading font-bold transition-colors duration-300 ${
                      isActive ? "text-primary" : "text-white"
                    }`}
                  >
                    {a.name}
                  </p>
                </div>
                {isActive && (
                  <motion.div
                    layoutId="activeArrow"
                    className="ml-auto"
                    initial={false}
                  >
                    <ArrowRight className="w-4 h-4 text-primary" />
                  </motion.div>
                )}
                {/* Active indicator bar */}
                {isActive && (
                  <motion.div
                    layoutId="activeBar"
                    className="absolute -bottom-0.5 left-4 right-4 h-0.5 bg-primary rounded-full"
                    initial={false}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </motion.button>
            );
          })}
        </motion.div>

        {/* Agent Display — Large Photo + Capabilities */}
        <motion.div
          variants={fade}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          custom={2}
        >
          <div className="glass-card-dark rounded-3xl shadow-lg overflow-hidden p-6 sm:p-8 lg:p-12">
            <AnimatePresence mode="wait">
              <motion.div
                key={agent.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center"
              >
                {/* Left: Photo */}
                <div className="flex justify-center">
                  <AgentPhoto agent={agent} />
                </div>

                {/* Right: Info + Capabilities */}
                <AgentCapabilities agent={agent} />
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Progress dots / auto-cycle indicator */}
        <div className="flex items-center justify-center gap-2 mt-6">
          {agents.map((a) => (
            <button
              key={a.id}
              onClick={() => setActive(a.id)}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                a.id === active ? "w-8 bg-primary" : "w-1.5 bg-white/30"
              }`}
              aria-label={`Switch to ${a.name}`}
              data-testid={`agent-dot-${a.id}`}
            />
          ))}
          {!paused && (
            <motion.span
              className="ml-2 text-[9px] text-muted-foreground/50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              auto-cycling
            </motion.span>
          )}
        </div>
      </div>
    </section>
  );
};
